import React, { useState, useEffect, useCallback } from 'react'
import {
  Container,
  Brand,
  Box,
  LogoBox,
  ContentInput,
  HideRecaptchaBadge,
  RecaptchaNotice,
  ThrottleNotice
} from './styles'
import { useAuth } from '@/hooks/useAuth'
import type { SignInResult, MfaMethod } from '@/hooks/useAuth'
import { toast } from 'react-toastify'
import { Formik, FormikHelpers } from 'formik'
import Head from 'next/head'
import * as Yup from 'yup'

import { Form, Button, Card } from 'react-bootstrap'

import { useRouter } from 'next/router'
import {
  LoginCaptchaProvider,
  isLoginCaptchaConfigured,
  useLoginCaptcha
} from './LoginCaptchaProvider'
import { MfaSetupStep } from './MfaSetupStep'
import { MfaVerifyStep } from './MfaVerifyStep'
import { useIsMounted } from './useIsMounted'

interface FormValues {
  email: string
  password: string
}

/** Passo visível do login. Estado local — nunca `localStorage`. */
type AuthStep = 'credentials' | 'mfa-setup' | 'mfa-verify'

/**
 * O challenge aberto pelo `POST /sessions`, vivo só enquanto a tela está
 * montada.
 *
 * NÃO vai para `localStorage`: ele é a metade do login que já foi aceita, e
 * persisti-lo deixaria um passe de segundo fator guardado no navegador à
 * espera de qualquer script que soubesse a chave. Recarregar a página perde o
 * challenge, e isso é o comportamento correto — o servidor o expira sozinho.
 */
interface MfaChallenge {
  challenge: string
  /**
   * Método deste challenge, como veio na resposta que o abriu.
   *
   * Mora junto do challenge de propósito: os dois nascem e morrem na mesma
   * resposta do `POST /sessions`. A configuração do assinante pode mudar entre dois
   * logins do mesmo usuário, então não há nada a cachear nem a derivar de um
   * login anterior — e manter o método fora deste objeto abriria espaço para
   * ele sobreviver a um `backToCredentials` e descrever o challenge errado.
   */
  method: MfaMethod
  /** Validade do código em minutos, só no modo e-mail. */
  timeoutMinutes?: number
  /**
   * E-mail usado nestas credenciais, para o texto do modo e-mail dizer para
   * onde o código foi. Vem do formulário, não da API.
   */
  email?: string
  otpauthUri?: string
  secretManual?: string
}

/**
 * Erros que MATAM o challenge: insistir no código não leva a lugar nenhum,
 * porque não há mais challenge do outro lado para validá-lo.
 *
 * - `MFA_CHALLENGE_GONE`: expirado, já consumido, ou queimado por ter esgotado
 *   as tentativas (a API responde este código quando `attempts_left` chega a 0).
 * - `TOO_MANY_ATTEMPTS`: a penalidade da chave `mfa` do throttle. O challenge
 *   até pode continuar de pé, mas toda tentativa seguinte leva 429 antes mesmo
 *   de o código ser conferido — manter a tela do código seria um campo que não
 *   aceita mais nada.
 *
 * Nos dois casos o caminho de volta é o mesmo: refazer o login do começo.
 */
const CHALLENGE_LOST_CODES = ['MFA_CHALLENGE_GONE', 'TOO_MANY_ATTEMPTS']

interface SendRecoveryValues {
  email: string
}

interface RecoveryValues {
  password: string
}
//

const BrandPanel: React.FC = () => (
  <Brand>
    <div className="brand-aurora" aria-hidden="true">
      <div data-ocix-glow="a" />
      <div data-ocix-glow="b" />
      <div data-ocix-glow="c" />
      <div className="brand-grain" />
    </div>
    <img
      className="brand-logo"
      src="/logo-ocix-branca.svg"
      alt="Plataforma OCIX"
    />
    <div className="brand-copy">
      <h1>
        Bem vindo
        <br />a BFC
      </h1>
      <div className="brand-tagline">
        <span>A parceria certa</span>
        <span>para a sua empresa</span>
        <span>está aqui</span>
      </div>
    </div>
    <p className="brand-signature">BFC Digital · Plataforma OCIX</p>
  </Brand>
)

/**
 * Atribuição exigida pelos termos do reCAPTCHA, já que o selo flutuante está
 * oculto por CSS. Sem captcha configurado não há selo nem script do Google
 * envolvido, então também não há o que atribuir.
 */
const CaptchaAttribution: React.FC = () => {
  if (!isLoginCaptchaConfigured) {
    return null
  }

  return (
    <RecaptchaNotice>
      Este site é protegido por reCAPTCHA. Aplicam-se a{' '}
      <a
        href="https://policies.google.com/privacy"
        target="_blank"
        rel="noopener noreferrer"
      >
        Política de Privacidade
      </a>{' '}
      e os{' '}
      <a
        href="https://policies.google.com/terms"
        target="_blank"
        rel="noopener noreferrer"
      >
        Termos de Serviço
      </a>{' '}
      do Google.
    </RecaptchaNotice>
  )
}

/**
 * Formata o tempo restante para leitura humana.
 *
 * A escada de penalidade vai até 1800 segundos, e "1799s" não comunica nada a
 * quem está tentando entrar -- obriga a pessoa a fazer a divisão de cabeça.
 * Abaixo de um minuto o segundo ainda é a unidade útil; acima disso, o minuto
 * é que é, com o segundo junto para o contador não parecer travado.
 */
function formatRemaining(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds}s`

  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return seconds === 0 ? `${minutes}min` : `${minutes}min ${seconds}s`
}

/**
 * Penalidade de 429 vinda da API.
 *
 * Guarda o instante de liberação (`retryAt`) e deriva dele os segundos
 * restantes, em vez de decrementar um contador: assim a conta continua certa
 * se a aba ficar em segundo plano e o navegador estrangular o `setInterval`.
 *
 * O contador é só informação de tela. Quem bloqueia é o servidor, e ele segue
 * bloqueando depois de um F5 — por isso o estado NÃO é persistido (fingir que
 * o recarregamento libera seria mentira) e, quando chega a zero, a tela apenas
 * reabre o botão: se a penalidade ainda estiver de pé, a API devolve outro 429
 * e o contador se rearma com o novo `retry_after`.
 */
function useThrottlePenalty() {
  const [retryAt, setRetryAt] = useState<number | null>(null)
  const [secondsLeft, setSecondsLeft] = useState(0)

  useEffect(() => {
    if (retryAt === null) {
      setSecondsLeft(0)
      return undefined
    }

    const tick = () => {
      const remaining = Math.ceil((retryAt - Date.now()) / 1000)

      if (remaining <= 0) {
        setRetryAt(null)
        setSecondsLeft(0)
        return
      }

      setSecondsLeft(remaining)
    }

    tick()
    const timer = setInterval(tick, 1000)

    // Cleanup no unmount e a cada novo `retryAt`: sem isso o 429 seguinte
    // deixaria dois intervals vivos, e sair da tela de login manteria um timer
    // chamando setState em componente desmontado.
    return () => clearInterval(timer)
  }, [retryAt])

  /**
   * Lê o `retry_after` de uma falha de credencial e arma a penalidade.
   *
   * Não emite toast: a mensagem já saiu uma única vez no interceptor do
   * `useAuth`. Aqui só se cuida do estado da tela.
   */
  const registerFailure = useCallback((error: any) => {
    // O interceptor rejeita com `error.response`; um erro cru de rede chega
    // inteiro. Aceitamos as duas formas.
    const response = error?.response || error

    if (response?.status !== 429) {
      return
    }

    const seconds = Number(response?.data?.retry_after)

    if (!seconds || isNaN(seconds) || seconds <= 0) {
      return
    }

    setRetryAt(Date.now() + seconds * 1000)
  }, [])

  return { secondsLeft, blocked: secondsLeft > 0, registerFailure }
}

const AuthPageContent: React.FC<
  Partial<{
    signedOutBecauseOfInactivity: boolean
  }>
> = ({ signedOutBecauseOfInactivity = false }) => {
  const { signIn, confirmMfa, sendRecovery, recovery, signOut, user } =
    useAuth()
  const [recoveryAccount, setRecoveryAccount] = useState(false)
  const [tokenRecovery, setTokenRecovery] = useState('')

  const [step, setStep] = useState<AuthStep>('credentials')
  const [mfa, setMfa] = useState<MfaChallenge | null>(null)
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null)
  const isMounted = useIsMounted()

  // Hook do captcha chamado AQUI, num filho do LoginCaptchaProvider. Chamado
  // no mesmo componente que monta o provider, leria o contexto de fora.
  const getLoginCaptcha = useLoginCaptcha()

  const { secondsLeft, blocked, registerFailure } = useThrottlePenalty()

  const {
    query: { t }
  } = useRouter()

  useEffect(() => {
    if (t) {
      setTokenRecovery(t as string)
    } else {
      setTokenRecovery('')
    }
  }, [t])

  const throttleNotice = blocked ? (
    <ThrottleNotice role="status" aria-live="polite">
      Muitas tentativas a partir desta origem. Novo envio liberado em{' '}
      <strong>{formatRemaining(secondsLeft)}</strong>.
    </ThrottleNotice>
  ) : null

  /**
   * Volta ao passo de e-mail/senha e descarta o challenge.
   *
   * Usado pelo botão "Voltar" e pelos erros que matam o challenge. Como os
   * passos são telas distintas, voltar desmonta o Formik do código e remonta o
   * de credenciais — os campos saem limpos sem que ninguém precise zerá-los.
   */
  const backToCredentials = useCallback(() => {
    setStep('credentials')
    setMfa(null)
    setAttemptsLeft(null)
  }, [])

  /**
   * Encaminha o resultado do `signIn` para o passo correspondente.
   *
   * O `email` não vem da resposta — a API não devolve o endereço e não deveria
   * (confirmar e-mails é enumeração de base). Vem de quem chamou: o campo que a
   * pessoa acabou de preencher, ou o `user.email` na tela de inatividade.
   */
  const enterMfaStep = useCallback(
    (result: SignInResult, email?: string) => {
      // A sessão já foi gravada em `signIn`; o AuthProvider troca a tela.
      if (result.state === 'logged_in') {
        return
      }

      setAttemptsLeft(null)

      if (result.state === 'mfa_setup_required') {
        /*
         * Defesa contra contrato inesperado.
         *
         * Pelo contrato, `mfa_setup_required` SEMPRE vem com `otpauth_uri` — e
         * é inalcançável com a válvula do e-mail aberta. Se ainda assim chegar
         * sem a URI, entrar no passo de vinculação renderizaria um QR vazio
         * (ou, com o segredo manual também ausente, uma caixa em branco) acima
         * de um campo de 6 dígitos que ninguém tem como preencher: a pessoa
         * ficaria presa numa tela sem saída, já tendo entregado a senha.
         *
         * Não há como seguir para o passo de código: o challenge é de
         * VINCULAÇÃO e o usuário não tem segredo nenhum para gerar um TOTP.
         * Então o honesto é não sair das credenciais, dizer que falhou e
         * descartar o challenge — o servidor o expira sozinho.
         */
        if (!result.otpauthUri) {
          toast.error(
            'Não foi possível iniciar a vinculação do aplicativo autenticador. Tente novamente em instantes; se o erro persistir, contate o suporte.'
          )
          backToCredentials()
          return
        }

        setMfa({
          challenge: result.challenge,
          method: result.method,
          email,
          otpauthUri: result.otpauthUri,
          secretManual: result.secretManual
        })
        setStep('mfa-setup')
        return
      }

      setMfa({
        challenge: result.challenge,
        method: result.method,
        timeoutMinutes: result.timeoutMinutes,
        email
      })
      setStep('mfa-verify')
    },
    [backToCredentials]
  )

  /**
   * Envia o código do autenticador e decide o que fazer com a recusa.
   *
   * Relança o erro para que o Formik do passo libere o botão e limpe o campo;
   * a mensagem em si já saiu uma vez no interceptor do `useAuth`.
   */
  const submitMfaCode = useCallback(
    async (code: string) => {
      if (!mfa) {
        backToCredentials()
        return
      }

      try {
        await confirmMfa({ challenge: mfa.challenge, code })
        // Nada de router.push: gravada a sessão, o AuthProvider troca esta
        // tela pelo layout do sistema sozinho.
      } catch (error) {
        // O interceptor rejeita com `error.response` (um AxiosResponse); um
        // erro cru de rede chega inteiro. Aceitamos as duas formas.
        const response = (error as any)?.response || error
        const code = response?.data?.code

        // Arma o contador regressivo se a recusa foi 429.
        registerFailure(error)

        if (CHALLENGE_LOST_CODES.includes(code) || response?.status === 429) {
          backToCredentials()
          throw error
        }

        const left = Number(response?.data?.attempts_left)
        setAttemptsLeft(Number.isFinite(left) ? left : null)

        throw error
      }
    },
    [mfa, confirmMfa, registerFailure, backToCredentials]
  )

  if (tokenRecovery !== '') {
    return (
      <Container>
        <Head>
          <title>BFC Digital - Recuperar Conta</title>
        </Head>
        <BrandPanel />
        <section>
          <Formik
            initialValues={{
              password: ''
            }}
            validationSchema={Yup.object({
              password: Yup.string().required('Senha obrigatória')
            })}
            onSubmit={async (
              values: RecoveryValues,
              { setSubmitting }: FormikHelpers<RecoveryValues>
            ) => {
              const { password } = values

              try {
                await recovery(tokenRecovery, password)
              } catch (error) {
                // Mensagem já exibida pelo interceptor do useAuth. O catch
                // existe para a rejeição não ficar sem tratamento.
              } finally {
                setSubmitting(false)
              }
            }}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              isSubmitting,
              errors,
              touched
            }) => (
              <Box onSubmit={handleSubmit}>
                <ContentInput>
                  <LogoBox>Digite uma nova senha para sua conta</LogoBox>
                  <Form.Group className="mb-2">
                    <Form.Label>Senha</Form.Label>
                    <Form.Control
                      name="password"
                      type="password"
                      placeholder="Senha"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password}
                      className="input-radius"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.password && touched.password && errors.password}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Button
                    disabled={isSubmitting}
                    type="submit"
                    className="d-block mt-3"
                    style={{ width: '100%' }}
                    variant="primary"
                    block
                  >
                    Recuperar Conta
                  </Button>
                </ContentInput>
              </Box>
            )}
          </Formik>
        </section>
      </Container>
    )
  }

  if (recoveryAccount) {
    return (
      <Container>
        <Head>
          <title>BFC Digital - Esqueci minha senha</title>
        </Head>
        <BrandPanel />
        <section>
          <Formik
            initialValues={{
              email: ''
            }}
            validationSchema={Yup.object({
              email: Yup.string()
                .email('Endereço de email inválido')
                .required('Email obrigatório')
            })}
            onSubmit={async (
              values: SendRecoveryValues,
              { setSubmitting }: FormikHelpers<SendRecoveryValues>
            ) => {
              const { email } = values

              try {
                await sendRecovery(email)
              } catch (error) {
                // TODO: Tratar mensagens de erro
                // toast.error(error.message)
              } finally {
                setSubmitting(false)
              }
            }}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              isSubmitting,
              errors,
              touched
            }) => (
              <Box onSubmit={handleSubmit}>
                <div>
                  <LogoBox>Digite o e-mail de sua conta</LogoBox>
                  <ContentInput>
                    <Form.Group className="mb-2">
                      <Form.Label>E-mail</Form.Label>
                      <Form.Control
                        name="email"
                        type="email"
                        placeholder="E-mail"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.email}
                        className="input-radius"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email && touched.email && errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Button
                      disabled={isSubmitting}
                      type="submit"
                      className="d-block mt-3"
                      style={{ width: '100%' }}
                      variant="primary"
                      block
                    >
                      Recuperar Conta
                    </Button>
                    <Button
                      type="button"
                      className="mt-2"
                      style={{ width: '100%' }}
                      variant="link"
                      onClick={() => setRecoveryAccount(false)}
                    >
                      Cancelar
                    </Button>
                  </ContentInput>
                </div>
              </Box>
            )}
          </Formik>
        </section>
      </Container>
    )
  }

  if (step !== 'credentials' && mfa) {
    const sharedProps = {
      onSubmit: submitMfaCode,
      onBack: backToCredentials,
      blocked,
      throttleNotice,
      attribution: <CaptchaAttribution />
    }

    return (
      <Container>
        <Head>
          <title>BFC Digital - Autenticação</title>
        </Head>
        <HideRecaptchaBadge />
        <BrandPanel />
        <section>
          {step === 'mfa-setup' ? (
            <MfaSetupStep
              {...sharedProps}
              otpauthUri={mfa.otpauthUri || ''}
              secretManual={mfa.secretManual || ''}
            />
          ) : (
            <MfaVerifyStep
              {...sharedProps}
              attemptsLeft={attemptsLeft}
              method={mfa.method}
              email={mfa.email}
              timeoutMinutes={mfa.timeoutMinutes}
            />
          )}
        </section>
      </Container>
    )
  }

  if (signedOutBecauseOfInactivity) {
    return (
      <Container>
        <Head>
          <title>BFC Digital - Autenticação</title>
        </Head>
        <HideRecaptchaBadge />
        <BrandPanel />
        <section>
          <Formik
            initialValues={{
              password: ''
            }}
            validationSchema={Yup.object({
              password: Yup.string().required('Senha obrigatória')
            })}
            onSubmit={async (
              values: {
                password: string
              },
              { setSubmitting }: FormikHelpers<FormValues>
            ) => {
              const { password } = values

              try {
                // Token novo também aqui: o do login anterior já expirou ou já
                // foi consumido.
                const gresponse = await getLoginCaptcha()

                /*
                 * `signedOutBecauseOfInactivity` NÃO vai mais no corpo. A API
                 * deixou de ler essa flag de propósito: ela vinha do cliente e
                 * dispensava o segundo fator, então qualquer um com a senha
                 * desligava o MFA mandando um booleano. Aqui ela só decide
                 * qual tela mostrar.
                 *
                 * Os três retornos são tratados: `mfa_setup_required` (admin
                 * resetou o autenticador na ociosidade), `mfa_token_required` e
                 * `logged_in` (assinante sem segundo fator; o AuthProvider troca a tela).
                 *
                 * O método também pode ter mudado durante a ociosidade: a
                 * configuração do assinante (`require_totp` / `require_mfa_token`)
                 * pode ser alterada a qualquer momento. Quem entrou
                 * por TOTP pode reentrar por e-mail, e é por isso que o
                 * `method` é lido desta resposta e não lembrado da anterior.
                 */
                enterMfaStep(
                  await signIn({
                    email: user.email,
                    password,
                    gresponse
                  }),
                  user.email
                )
              } catch (error) {
                // A mensagem sai no interceptor; aqui só se arma o contador do
                // 429, quando for o caso.
                registerFailure(error)
              } finally {
                // `logged_in` desmonta a tela antes deste finally; ver useIsMounted.
                if (isMounted()) {
                  setSubmitting(false)
                }
              }
            }}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              isSubmitting,
              errors,
              touched
            }) => (
              <Box onSubmit={handleSubmit}>
                <div>
                  <LogoBox>Tempo de sessão expirado</LogoBox>
                  <ContentInput>
                    <Form.Group className="mb-2">
                      <Form.Label>E-mail</Form.Label>
                      <Card className="p-2">{user.email}</Card>
                    </Form.Group>
                    <Form.Group className="mb-0">
                      <Form.Label>Senha</Form.Label>
                      <Form.Control
                        name="password"
                        type="password"
                        placeholder="*******"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.password}
                        className="input-radius"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.password && touched.password && errors.password}
                      </Form.Control.Feedback>
                    </Form.Group>
                    {throttleNotice}
                    <Button
                      disabled={isSubmitting || blocked}
                      type="submit"
                      className="d-block mt-3"
                      style={{ width: '100%' }}
                      variant="primary"
                      block
                    >
                      Entrar
                    </Button>
                    <Button
                      type="button"
                      className="mt-2"
                      style={{ width: '100%' }}
                      variant="link"
                      onClick={() => signOut()}
                    >
                      Sair
                    </Button>
                    <CaptchaAttribution />
                  </ContentInput>
                </div>
              </Box>
            )}
          </Formik>
        </section>
      </Container>
    )
  }

  return (
    <Container>
      <Head>
        <title>BFC Digital - Autenticação</title>
      </Head>
      <HideRecaptchaBadge />
      <BrandPanel />
      <section>
        <Formik
          initialValues={{
            email: '',
            password: ''
          }}
          validationSchema={Yup.object({
            email: Yup.string()
              .email('Endereço de email inválido')
              .required('Email obrigatório'),
            password: Yup.string().required('Senha obrigatória')
          })}
          onSubmit={async (
            values: FormValues,
            { setSubmitting }: FormikHelpers<FormValues>
          ) => {
            const { email, password } = values

            try {
              // Um token por submit. O da tentativa anterior, se houve, já foi
              // gasto ou expirou.
              const gresponse = await getLoginCaptcha()

              /*
               * O `POST /sessions` devolve um challenge (VINCULAR o autenticador
               * ou só informar o código) ou `logged_in`, que encerra o login
               * aqui mesmo: o AuthProvider troca a tela e `enterMfaStep` não age.
               *
               * A resposta diz também QUAL segundo fator está em vigor
               * (`method`), e o e-mail digitado segue junto para que a tela do
               * modo e-mail possa dizer para onde o código foi.
               */
              enterMfaStep(await signIn({ email, password, gresponse }), email)
            } catch (error) {
              registerFailure(error)
            } finally {
              // `logged_in` desmonta a tela antes deste finally; ver useIsMounted.
              if (isMounted()) {
                setSubmitting(false)
              }
            }
          }}
        >
          {({
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            isSubmitting,
            errors,
            touched
          }) => (
            <Box onSubmit={handleSubmit}>
              <div>
                <LogoBox>Entrar</LogoBox>
                <ContentInput>
                  <Form.Group className="mb-2">
                    <Form.Label>E-mail</Form.Label>
                    <Form.Control
                      name="email"
                      type="email"
                      disabled={signedOutBecauseOfInactivity}
                      placeholder="E-mail"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      className="input-radius"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email && touched.email && errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-0">
                    <Form.Label>Senha</Form.Label>
                    <Form.Control
                      name="password"
                      type="password"
                      placeholder="*******"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password}
                      className="input-radius"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.password && touched.password && errors.password}
                    </Form.Control.Feedback>
                  </Form.Group>
                  {throttleNotice}
                  <Button
                    disabled={isSubmitting || blocked}
                    type="submit"
                    className="d-block mt-3"
                    style={{ width: '100%' }}
                    variant="primary"
                    block
                  >
                    Entrar
                  </Button>

                  <Button
                    type="button"
                    className="mt-2"
                    style={{ width: '100%' }}
                    variant="link"
                    onClick={() => setRecoveryAccount(true)}
                  >
                    Esqueci minha senha
                  </Button>
                  <CaptchaAttribution />
                </ContentInput>
              </div>
            </Box>
          )}
        </Formik>
      </section>
    </Container>
  )
}

/**
 * O provider fica NESTE nível e o formulário num componente filho. É a única
 * disposição que funciona: `useGoogleReCaptcha()` chamado no mesmo componente
 * que renderiza `GoogleReCaptchaProvider` leria o contexto do pai e devolveria
 * `executeRecaptcha` indefinido, derrubando o captcha em silêncio.
 */
export const AuthPage: React.FC<
  Partial<{
    signedOutBecauseOfInactivity: boolean
  }>
> = ({ signedOutBecauseOfInactivity = false }) => (
  <LoginCaptchaProvider>
    <AuthPageContent
      signedOutBecauseOfInactivity={signedOutBecauseOfInactivity}
    />
  </LoginCaptchaProvider>
)
